import React from "react";
import ContractDashboard, { fromWei, formatDuration } from "./shared/ContractDashboard";
import marketplaceFunctions from "./functions/marketplaceFunctions";
import { nemiMarketplace, nemiMarketplaceAbi } from "./Contract";

/**
 * Nemi NFT Marketplace — trade slots, the sale queue, auto-trade bots and income splits.
 * Every function of the nemiMarketplace contract is available through the tabs below.
 */
export default function MarketplacePage() {
  const overview = [
    {
      key: "totalTradeIds",
      label: "Total Trade IDs",
      icon: "🎫",
      method: "totalTradeIds",
      color: "#ff00aa",
    },
    {
      key: "maxTradeSupply",
      label: "Max Trade Supply",
      icon: "📈",
      method: "maxTradeSupply",
      color: "#45b7d1",
    },
    {
      key: "tradeBasePrice",
      label: "Trade Base Price",
      icon: "💲",
      method: "tradeBasePrice",
      format: (v) => `${fromWei(v)} USDT`,
      color: "#00ff88",
    },
    {
      key: "myTrades",
      label: "Your Trades",
      icon: "👤",
      color: "#ffaa00",
      compute: async ({ contract, account }) =>
        String(await contract.methods.getUserOwnedTradeLength(account).call()),
    },
    {
      key: "myQueued",
      label: "Your Queued Trades",
      icon: "📋",
      color: "#6be0d7",
      compute: async ({ contract, account }) => {
        const queue = await contract.methods.getUserTradeQueue(account).call();
        return String((queue || []).length);
      },
    },
    {
      key: "myNextSale",
      label: "Your Next Sale",
      icon: "⏱️",
      color: "#00ccff",
      compute: async ({ contract, account }) => {
        const next = await contract.methods.getUserNextSale(account).call();
        return String(next);
      },
    },
    {
      key: "queueLength",
      label: "Sale Queue Length",
      icon: "🧾",
      method: "tradeQueueLength",
      color: "#ff8800",
    },
    {
      key: "buyCount",
      label: "Total Buys",
      icon: "🛒",
      method: "buyCount",
      color: "#00ff88",
    },
    {
      key: "botCount",
      label: "Registered Bots",
      icon: "🤖",
      method: "botCount",
      color: "#45b7d1",
    },
    {
      key: "sellPercent",
      label: "Sell Percent",
      icon: "📊",
      method: "sellPercent",
      format: (v) => `${v}%`,
      color: "#ff00aa",
    },
    {
      key: "maxSalePrice",
      label: "Max Sale Price",
      icon: "💰",
      method: "maxSalePrice",
      format: (v) => `${fromWei(v)} USDT`,
      color: "#ffaa00",
    },
    {
      key: "globalPeriod",
      label: "Global Period",
      icon: "⌛",
      method: "globalPeriod",
      format: (v) => formatDuration(v),
      color: "#6be0d7",
    },
    {
      key: "tradeDeadDuration",
      label: "Trade Dead Duration",
      icon: "☠️",
      method: "tradeDeadDuration",
      format: (v) => formatDuration(v),
      color: "#ff6b6b",
    },
    {
      key: "autoTradeFee",
      label: "Auto Trade Fee",
      icon: "🤖",
      color: "#00ccff",
      compute: async ({ contract }) => {
        const [fee, enabled] = await Promise.all([
          contract.methods.autoTradeFee().call(),
          contract.methods.isAutoTradeFee().call(),
        ]);
        // Stored as raw contract units — shown exactly as the contract holds it.
        return enabled ? String(fee) : "Disabled";
      },
    },
    {
      key: "contractUsdt",
      label: "Contract USDT",
      icon: "🏦",
      color: "#26a17b",
      format: (v) => `${v} USDT`,
      compute: async ({ getTokenContract, address }) =>
        fromWei(await getTokenContract("USDT").methods.balanceOf(address).call()),
    },
    {
      key: "paused",
      label: "Contract Status",
      icon: "⚡",
      method: "paused",
      format: (v) => (v === true || v === "true" ? "PAUSED" : "ACTIVE"),
      color: "#ff6b6b",
    },
  ];

  return (
    <ContractDashboard
      title="Nemi NFT Marketplace"
      subtitle="Trade slots, the sale queue, auto-trade bots and income distribution"
      icon="🎨"
      accent="#ff00aa"
      address={nemiMarketplace}
      abi={nemiMarketplaceAbi}
      functions={marketplaceFunctions}
      overview={overview}
      approvalTokens={["USDT", "NEMI"]}
    />
  );
}
